import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ReturnDialog({ open, onOpenChange, sale, canRestock, onSubmit }) {
  const { t, fmtCurrency } = useI18n();
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [restock, setRestock] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && sale) {
      const init = {};
      (sale.lines || []).forEach((l) => {
        const remaining = Number(l.quantity) - Number(l.returned_qty || 0);
        init[l.id] = remaining > 0 ? String(remaining) : "0";
      });
      setQty(init); setReason(""); setRestock(false); setRefundMethod("cash");
    }
  }, [open, sale]);

  if (!sale) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const lines = (sale.lines || [])
        .map((l) => ({ line_id: l.id, quantity: String(qty[l.id] || "0") }))
        .filter((l) => Number(l.quantity) > 0);
      await onSubmit({ lines, reason, refund_method: refundMethod, restock });
    } finally {
      setBusy(false);
    }
  };

  const total = (sale.lines || []).reduce((s, l) => s + Number(qty[l.id] || 0) * (Number(l.line_gross) / (Number(l.quantity) || 1)), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pos-return-dialog">
        <DialogHeader><DialogTitle>{t("pos.return_title")} — {sale.receipt_number}</DialogTitle></DialogHeader>
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {(sale.lines || []).map((l) => {
            const remaining = Number(l.quantity) - Number(l.returned_qty || 0);
            return (
              <div key={l.id} className="flex items-center gap-2 rounded border border-slate-200 p-2 dark:border-slate-700" data-testid="pos-return-line">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{l.description}</div>
                  <div className="text-xs text-slate-500">{l.sku} · {t("pos.quantity")} {l.quantity} · {fmtCurrency(l.line_gross)}</div>
                </div>
                <Input type="number" step="0.001" min="0" max={remaining} value={qty[l.id] || "0"}
                  onChange={(e) => setQty((q) => ({ ...q, [l.id]: e.target.value }))}
                  className="w-24 tabular-nums" disabled={remaining <= 0} data-testid={`pos-return-qty-${l.id}`} />
              </div>
            );
          })}
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("pos.reason")} *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pos-return-reason" />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 space-y-1.5">
              <Label>{t("pos.refund_method")}</Label>
              <Select value={refundMethod} onValueChange={setRefundMethod}>
                <SelectTrigger data-testid="pos-return-refund-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t("pos.cash")}</SelectItem>
                  <SelectItem value="card">{t("pos.card")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {canRestock && (
              <label className="flex items-center gap-2 pt-6 text-sm">
                <Switch checked={restock} onCheckedChange={setRestock} data-testid="pos-return-restock" />{t("pos.restock")}
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-return-cancel">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || !reason} data-testid="pos-return-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("pos.process_return")} ({fmtCurrency(total)})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
